/**
 * Filter memory for the four dashboards: which URL params are remembered on the
 * account and restored when the page is opened without them.
 *
 * Only keys that are actually in the URL are listed. State that lives in
 * component `useState` cannot be remembered by a URL mechanism:
 * - /management Financial Analytics (window, subject, members, figure mode,
 *   currency, region) and Top Movers (currency, filters, cycles) are local state.
 * - /accounts-dashboard search, page and sort are local state per section.
 * - /university-partnerships per-card search and page are local state.
 *
 * Keys deliberately LEFT OUT:
 * - `removed` (/operations, Client Deliverables pill): the Removed view is for
 *   restoring a row, not a place to live (ClientDeliverablesPage SAVED_FILTER_KEYS).
 * - UP `leadsStatus`, `companiesStatus`, `{deals,quotes,invoices}Country` and the
 *   other card x kind combinations no card offers: the page parses them but
 *   nothing reads them.
 */
import type { FilterMemoryRule } from './types';

export const FILTER_MEMORY_DASHBOARDS: readonly FilterMemoryRule[] = [
  {
    path: '/management',
    keep: [
      'tab', // tab: revenue-company | movers | revenue-product; absent = Financial Analytics
      // Revenue by Company tab (prefixed `rc`)
      'rcCurrency', // base currency picker
      'rcCycle', // filter: cycle (by name)
      'rcRegion', // filter: billing region
      'rcProduct', // filter: product
      'rcQ', // search
      'rcMin', // filter: revenue floor
      'rcSort', // sort
      'rcTrading', // filter: `current` = only companies still trading
      // Revenue by Product tab (prefixed `rp`)
      'rpCurrency', // base currency picker
      'rpQ', // search
      'rpRegion', // filter: billing region
      'rpCycle', // filter: cycle
      'rpUncoded', // toggle: `0` = exclude uncatalogued lines; absent = include
    ],
  },
  {
    path: '/accounts-dashboard',
    keep: [
      'section', // tab: invoices | future; absent = quotes
      'view', // `table`; absent = sheet (default)
      // shared by all three sections
      'companyId', // filter: company
      // quotes + invoices
      'contactId', // filter: contact
      'lineProjectId', // filter: project on any line
      'accountLeadId', // filter: account lead
      'currency', // filter: currency
      'amountMin', // filter: amount lower bound
      'amountMax', // filter: amount upper bound
      // quotes
      'stageId', // filter: quote stage
      'issueDateAfter', // filter: issue date from
      'issueDateBefore', // filter: issue date to
      'validTillAfter', // filter: expiry from
      'validTillBefore', // filter: expiry to
      'draft', // flag filter: drafts
      'awaitingSignature', // flag filter: awaiting signature
      'acceptedUnbilled', // flag filter: accepted, not billed
      // invoices
      'statusId', // filter: invoice status
      'quoteId', // filter: source quote
      'billDateAfter', // filter: bill date from
      'billDateBefore', // filter: bill date to
      'dueDateAfter', // filter: due date from
      'dueDateBefore', // filter: due date to
      'unsettled', // flag filter: unsettled
      'overdue', // flag filter: overdue
      'overdue90Plus', // flag filter: 90+ days overdue
      'dueIn30Days', // flag filter: due in the next 30 days
      // future billables
      'plannedIn30Days', // flag filter: planned in the next 30 days
    ],
  },
  {
    path: '/university-partnerships',
    keep: [
      // per-card sort (absent = the card's default)
      'leadsSort', // sort: Leads
      'dealsSort', // sort: Deals
      'quotesSort', // sort: Quotes
      'invoicesSort', // sort: Invoices
      'companiesSort', // sort: Companies
      // Leads card (COMPANY_FILTERS)
      'leadsCountry', // filter: country ids, comma-separated
      'leadsSource', // filter: source ids
      'leadsCompanyType', // filter: company type ids
      'leadsLastNoteAfter', // filter: last note from
      'leadsLastNoteBefore', // filter: last note to
      // Companies card (COMPANIES_FILTERS)
      'companiesCountry', // filter: country ids
      'companiesSource', // filter: source ids
      'companiesCompanyType', // filter: company type ids
      'companiesPurchasedCycle', // filter: cycles purchased in
      'companiesParticipatedCycle', // filter: cycles participated in
      'companiesLastNoteAfter', // filter: last note from
      'companiesLastNoteBefore', // filter: last note to
      // document cards (STATUS_FILTER)
      'dealsStatus', // filter: deal stage ids
      'quotesStatus', // filter: quote stage ids
      'invoicesStatus', // filter: invoice status ids
    ],
  },
  {
    path: '/operations',
    keep: [
      'tab', // pill: billables | companies; absent = deliverables
      // Client Deliverables pill
      'deliverables', // sub-tab: `capacity` = Project Capacity; absent = tracker
      'capacityCycleId', // Project Capacity filter: cycle
      'capacityProjectId', // Project Capacity filter: project
      'companyId', // filter: company (also Future Billables)
      'productId', // filter: product (also Future Billables)
      'projectId', // filter: project (also Future Billables)
      'intakeId', // filter: intake
      'cycleId', // filter: cycle
      'regionId', // filter: region (also Companies)
      'source', // filter: row source
      'billingStatus', // filter: billing status
      'outstanding', // filter: outstanding
      'view', // Deliverables `table` (absent = sheet); Companies `board` (absent = table)
      // Future Billables pill
      'currency', // filter: currency
      'kind', // filter: quote or invoice
      'amountMin', // filter: amount lower bound
      'amountMax', // filter: amount upper bound
      'plannedIn30Days', // flag filter: planned in the next 30 days
      'display', // view: sheet or table
      // Companies pill
      'stageId', // filter: company stage
      'accountLeadId', // filter: account lead
      'clientSuccessManagerId', // filter: client success manager
      'countryId', // filter: country
      'sourceId', // filter: company source
      'companyTypeId', // filter: company type
      'participatedCycleId', // filter: cycles participated in
      'purchasedCycleId', // filter: cycles purchased in
    ],
  },
];
